import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ManageSubscriptionButton from '../components/ManageSubscriptionButton';
import supabase from '../lib/supabase';
import '../styles/pages/AccountPage.css';

const AccountPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(true);
  const [feedbackError, setFeedbackError] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const planNameMapping = {
    // Example: 'price_1RDBdfGOH0YCKqwhXshaWrA7': 'Free Trial Plan',
    // Add your actual price IDs and names here
  };

  const fetchSubscription = async (userId) => {
    setSubscriptionLoading(true);
    setSubscription(null);
    setSubscriptionError(null);

    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('id, user_id, status, price_id, product_name, current_period_end, cancel_at_period_end')
        .eq('user_id', userId)
        .in('status', ['trialing', 'active'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        throw new Error(error.message || 'Supabase query failed with an error object');
      }

      if (data) {
        setSubscription(data);
      } else {
        setSubscription(null);
      }

    } catch (e) {
      setSubscriptionError(e.message || 'An unknown error occurred while fetching your subscription.');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const fetchFeedback = async (userId) => {
    setFeedbackLoading(true);
    setFeedbackItems([]);
    setFeedbackError(null);
    try {
      const { data, error } = await supabase
        .from('feedback')
        .select('id, created_at, feedback_type, message, status')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }
      setFeedbackItems(data || []);
    } catch (e) {
      console.error('Error fetching feedback:', e);
      setFeedbackError(e.message || 'An unknown error occurred while fetching your feedback.');
    } finally {
      setFeedbackLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return; 

    if (user && user.id) {
      fetchSubscription(user.id);
      fetchFeedback(user.id); 
    } else {
      setSubscriptionLoading(false); 
      setFeedbackLoading(false); 
    }
  }, [user, authLoading]); 

  if (authLoading || subscriptionLoading || feedbackLoading) { 
    return <div className="container text-center mt-5"><p>Loading account details...</p></div>;
  }

  if (!user) {
    return (
      <div className="container mt-5">
        <p>Please log in to view your account details.</p>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4 account-page-container">
      <h2 className="mb-4">My Account</h2>
      <div className="card mb-4">
        <div className="card-body">
          <h5 className="card-title">User Information</h5>
          <p className="card-text"><strong>Email:</strong> {user.email}</p>
        </div>
      </div>

      {subscriptionError && (
        <div className="alert alert-danger" role="alert">
          Error: {subscriptionError}
        </div>
      )}

      {subscription && (
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">Subscription Details</h5>
            <p className="card-text">
              <strong>Status:</strong> <span style={{ textTransform: 'capitalize' }}>
                {subscription.status === 'trialing' ? 'Trial' : subscription.status}
              </span>
            </p>
            <p className="card-text">
              <strong>Plan:</strong> {subscription.product_name || planNameMapping[subscription.price_id] || subscription.price_id}
            </p>
            {subscription.status === 'trialing' && subscription.current_period_end && (
              <p className="card-text">
                <strong>Trial Ends:</strong> {formatDate(subscription.current_period_end)}
              </p>
            )}
            {subscription.status !== 'trialing' && subscription.current_period_end && (
                 <p className="card-text">
                    <strong>Current Period Ends:</strong> {formatDate(subscription.current_period_end)}
                 </p>
            )}
            {subscription.cancel_at_period_end && (
              <p className="text-warning">
                Your subscription is set to cancel at the end of the current period ({formatDate(subscription.current_period_end)}).
              </p>
            )}
            <ManageSubscriptionButton />
          </div>
        </div>
      )}

      {!subscription && !subscriptionError && (
        <div className="card mb-4 no-subscription-card">
          <div className="card-body">
            <p>No active subscription found.</p>
          </div>
        </div>
      )}

      {/* Feedback Section */}
      <div className="card mb-4">
        <div className="card-body">
          <h5 className="card-title">My Feedback Submissions</h5>
          {feedbackError && (
            <div className="alert alert-danger" role="alert">
              Error loading feedback: {feedbackError}
            </div>
          )}
          {!feedbackLoading && !feedbackError && feedbackItems.length === 0 && (
            <p>You have not submitted any feedback yet.</p>
          )}
          {!feedbackLoading && !feedbackError && feedbackItems.length > 0 && (
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Type</th>
                    <th scope="col">Message</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {feedbackItems.map(item => (
                    <tr key={item.id}>
                      <td>{formatDate(item.created_at)}</td>
                      <td style={{ textTransform: 'capitalize' }}>{item.feedback_type}</td>
                      <td>{item.message}</td>
                      <td style={{ textTransform: 'capitalize', fontWeight: 'bold' }}>{item.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default AccountPage;
